import type { ParamRef } from "./controls";

// A big vertical fine-tune slider docked on the right edge. Tapping a parameter
// (in the controls) binds it here; this slider then drives that one parameter
// with a large, precise control. The small inline sliders stay for quick grabs;
// this one mirrors the selected parameter (log scale for frequency-type params).
//
// Mirroring used to be a permanent rAF poll of ref.get(). It now only runs
// while a gesture is in flight — the only way a bound param moves behind our
// back is someone dragging a fader or pressing a key — and it writes to the
// DOM only when the value actually changed. Idle (or hidden, which is every
// phone: the slider is display:none under 768 px) it holds no frame at all.

/** Keep mirroring this long after the gesture ends, for settling/animation. */
const TAIL_MS = 350;

export class ContextSlider {
  private readonly el: HTMLElement;
  private readonly labelEl: HTMLElement;
  private readonly valueEl: HTMLElement;
  private readonly track: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly thumb: HTMLElement;
  private ref: ParamRef | null = null;
  private dragging = false;
  private raf = 0;
  private until = 0;
  private last = NaN;

  constructor(parent: HTMLElement) {
    this.el = document.createElement("div");
    this.el.className = "ctx-slider";

    this.labelEl = document.createElement("div");
    this.labelEl.className = "ctx-label";
    this.labelEl.textContent = "—";

    this.track = document.createElement("div");
    this.track.className = "ctx-track";
    this.fill = document.createElement("div");
    this.fill.className = "ctx-fill";
    this.thumb = document.createElement("div");
    this.thumb.className = "ctx-thumb";
    this.track.append(this.fill, this.thumb);

    this.valueEl = document.createElement("div");
    this.valueEl.className = "ctx-value";

    this.el.append(this.labelEl, this.track, this.valueEl);
    parent.append(this.el);

    this.track.addEventListener("pointerdown", (e) => {
      if (!this.ref) return;
      e.preventDefault();
      this.track.setPointerCapture(e.pointerId);
      this.dragging = true;
      this.setFromY(e.clientY);
    });
    this.track.addEventListener("pointermove", (e) => {
      if (this.dragging) this.setFromY(e.clientY);
    });
    const end = (): void => {
      this.dragging = false;
    };
    this.track.addEventListener("pointerup", end);
    this.track.addEventListener("pointercancel", end);

    // A fader (or a drag-chip, or a key) anywhere on the page can move the
    // bound param, so any gesture opens a short mirroring window.
    const opts = { passive: true, capture: true } as const;
    document.addEventListener("pointerdown", this.pump, opts);
    document.addEventListener("pointermove", this.onGestureMove, opts);
    document.addEventListener("pointerup", this.pump, opts);
    document.addEventListener("pointercancel", this.pump, opts);
    document.addEventListener("keydown", this.pump, opts);
    document.addEventListener("keyup", this.pump, opts);
    document.addEventListener("wheel", this.pump, opts);

    this.sync();
  }

  bind(ref: ParamRef): void {
    this.ref = ref;
    this.labelEl.textContent = ref.label;
    this.el.classList.add("active");
    this.last = NaN;
    this.sync();
  }

  /** Repaint from the bound param now. Only needed if something changes a
   *  param outside of a user gesture — bind() and dragging sync themselves. */
  refresh(): void {
    this.sync();
  }

  /** Label of the currently bound param, or null. Lets a caller hand the slider
   *  back to Master when the param it was showing goes away. */
  boundLabel(): string | null {
    return this.ref?.label ?? null;
  }

  unbind(): void {
    this.ref = null;
    this.last = NaN;
    this.labelEl.textContent = "—";
    this.valueEl.textContent = "";
    this.fill.style.height = "0%";
    this.el.classList.remove("active");
  }

  private frac(v: number): number {
    const ref = this.ref!;
    if (ref.scale === "log") return Math.log(v / ref.min) / Math.log(ref.max / ref.min);
    return (v - ref.min) / (ref.max - ref.min);
  }

  private value(f: number): number {
    const ref = this.ref!;
    const raw = ref.scale === "log" ? ref.min * Math.pow(ref.max / ref.min, f) : ref.min + f * (ref.max - ref.min);
    const stepped = Math.round(raw / ref.step) * ref.step;
    return Math.min(ref.max, Math.max(ref.min, stepped));
  }

  private setFromY(clientY: number): void {
    if (!this.ref) return;
    const r = this.track.getBoundingClientRect();
    const f = 1 - Math.min(1, Math.max(0, (clientY - r.top) / r.height));
    this.ref.set(this.value(f));
    this.sync();
  }

  /** Paint the bound value — a no-op unless it actually moved. */
  private sync(): boolean {
    if (!this.ref) return false;
    const v = this.ref.get();
    if (v === this.last) return false;
    this.last = v;
    const f = Math.min(1, Math.max(0, this.frac(v)));
    this.thumb.style.top = `${(1 - f) * 100}%`;
    this.fill.style.height = `${f * 100}%`;
    this.valueEl.textContent = this.ref.fmt(v);
    return true;
  }

  /** offsetWidth is 0 while `.ctx-slider { display: none }` — i.e. on phones,
   *  where this thing never needs to mirror anything. */
  private mirrorable(): boolean {
    return !!this.ref && !document.hidden && this.el.offsetWidth > 0;
  }

  /** Moves only count while a button/finger is down — a hovering mouse must
   *  not keep the loop alive. */
  private onGestureMove = (e: PointerEvent): void => {
    if (e.buttons !== 0) this.pump();
  };

  /** Open (or extend) the mirroring window. The mirrorable() layout read only
   *  happens when the loop is idle, so a drag never thrashes layout. */
  private pump = (): void => {
    this.until = performance.now() + TAIL_MS;
    if (this.raf || !this.mirrorable()) return;
    this.raf = requestAnimationFrame(this.mirror);
  };

  private mirror = (): void => {
    this.raf = 0;
    if (this.sync()) this.until = performance.now() + TAIL_MS;
    // Gesture over and the value has settled → drop the frame entirely.
    // Any further movement pumps it straight back on the next event.
    if (!this.ref || document.hidden || performance.now() > this.until) return;
    this.raf = requestAnimationFrame(this.mirror);
  };
}
